import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { isAdminRole } from '../../lib/rolePermissions';
import { formatUsd, utcMonthKey } from '../../lib/aiUsageSpend';
import {
  AI_SUBSCRIPTION_PROVIDERS,
  COST_CATEGORY_SHIFTYPM_PLAN,
  newSubscriptionId,
  normalizeAiSubscriptions,
  normalizeMonthlyCostUsd,
  sumActiveSubscriptionUsd,
  syncSubscriptionCostEntries,
  writeManualCostEntry,
} from '../../lib/aiSubscriptions';

/**
 * @param {object} row
 * @returns {object}
 */
function emptyDraft(row = {}) {
  return {
    id: row.id || newSubscriptionId(),
    provider: row.provider || 'google',
    planName: row.planName || '',
    monthlyCostUsd: row.monthlyCostUsd != null ? String(row.monthlyCostUsd) : '',
    billingDayOfMonth:
      row.billingDayOfMonth != null ? String(row.billingDayOfMonth) : '',
    active: row.active !== false,
    notes: row.notes || '',
  };
}

/**
 * Fixed AI subscription costs + manual ShiftyPM plan ledger entry.
 * Kept separate from usage budgets / alerts.
 *
 * @param {{ companyId?: string | null, userRole?: string | null }} props
 */
export default function AiSubscriptionsSettings({ companyId, userRole }) {
  const resolvedCompanyId =
    typeof companyId === 'string' ? companyId.trim() : '';
  const allowed = Boolean(resolvedCompanyId && isAdminRole(userRole));

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(allowed);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [manualAmount, setManualAmount] = useState('');
  const [manualPlanName, setManualPlanName] = useState('ShiftyPM plan');
  const [manualSaving, setManualSaving] = useState(false);

  const load = useCallback(async () => {
    if (!allowed) {
      setDrafts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const snap = await getDoc(doc(db, 'companies', resolvedCompanyId));
      const settings = snap.exists() ? snap.data()?.settings || {} : {};
      const rows = normalizeAiSubscriptions(settings.aiSubscriptions);
      setDrafts(rows.map((r) => emptyDraft(r)));
    } catch (err) {
      console.error('AiSubscriptionsSettings load:', err);
      setError(err?.message || 'Could not load subscriptions.');
      setDrafts([]);
    } finally {
      setLoading(false);
    }
  }, [allowed, resolvedCompanyId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeTotal = useMemo(() => {
    const normalized = drafts.map((d) => ({
      ...d,
      monthlyCostUsd: normalizeMonthlyCostUsd(d.monthlyCostUsd),
    }));
    return sumActiveSubscriptionUsd(normalized);
  }, [drafts]);

  const updateDraft = (id, patch) => {
    setDrafts((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  };

  const handleSave = async () => {
    if (!allowed) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const next = [];
      for (const d of drafts) {
        const monthlyCostUsd = normalizeMonthlyCostUsd(d.monthlyCostUsd);
        if (monthlyCostUsd == null) {
          setError(
            `Invalid cost for “${d.planName || 'Untitled'}”. Enter 0 or a positive amount.`,
          );
          setSaving(false);
          return;
        }
        if (!String(d.planName || '').trim()) {
          setError('Each subscription needs a plan name.');
          setSaving(false);
          return;
        }
        const billingRaw = String(d.billingDayOfMonth || '').trim();
        const billingDay =
          billingRaw === '' ? null : Number(billingRaw);
        if (
          billingDay != null &&
          (!Number.isFinite(billingDay) || billingDay < 1 || billingDay > 28)
        ) {
          setError('Billing day must be 1–28 or blank.');
          setSaving(false);
          return;
        }
        next.push({
          id: d.id || newSubscriptionId(),
          provider: d.provider || 'other',
          planName: String(d.planName).trim().slice(0, 120),
          monthlyCostUsd,
          currency: 'USD',
          billingDayOfMonth:
            billingDay == null ? null : Math.floor(billingDay),
          active: d.active !== false,
          notes: String(d.notes || '').trim().slice(0, 500) || null,
          updatedAt: new Date().toISOString(),
        });
      }

      await updateDoc(doc(db, 'companies', resolvedCompanyId), {
        'settings.aiSubscriptions': next,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      });

      await syncSubscriptionCostEntries(db, resolvedCompanyId, next, {
        uid: auth.currentUser?.uid || null,
        monthsAhead: 1,
      });

      setDrafts(next.map((r) => emptyDraft(r)));
      setMessage(
        `Saved ${next.length} subscription${next.length === 1 ? '' : 's'}. ` +
          `Active monthly total ${formatUsd(sumActiveSubscriptionUsd(next))}. ` +
          `Ledger sync skips rewriting existing ${utcMonthKey()} rows.`,
      );
    } catch (err) {
      console.error('AiSubscriptionsSettings save:', err);
      setError(err?.message || 'Could not save subscriptions.');
    } finally {
      setSaving(false);
    }
  };

  const handleManualShiftyPm = async () => {
    if (!allowed) return;
    const amountUsd = normalizeMonthlyCostUsd(manualAmount);
    if (amountUsd == null) {
      setError('Enter a valid ShiftyPM plan amount.');
      return;
    }
    setManualSaving(true);
    setError('');
    setMessage('');
    try {
      await writeManualCostEntry(db, resolvedCompanyId, {
        category: COST_CATEGORY_SHIFTYPM_PLAN,
        amountUsd,
        periodMonth: utcMonthKey(),
        provider: 'shiftypm',
        planName: String(manualPlanName || 'ShiftyPM plan').trim().slice(0, 120),
        uid: auth.currentUser?.uid || null,
      });
      setManualAmount('');
      setMessage(
        `Logged ShiftyPM plan cost ${formatUsd(amountUsd)} for ${utcMonthKey()}.`,
      );
    } catch (err) {
      console.error('manual shiftypm cost:', err);
      setError(err?.message || 'Could not write ShiftyPM cost entry.');
    } finally {
      setManualSaving(false);
    }
  };

  if (!allowed) return null;

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <SubscriptionsOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                AI subscriptions (fixed)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recurring plan fees (Gemini Pro, Claude Team, etc.). These are
                informational and do not change usage budgets or 80%/100% alerts.
              </Typography>
            </Box>
          </Stack>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}
          {message ? (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
              {message}
            </Alert>
          ) : null}

          {loading ? (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Monthly $</TableCell>
                    <TableCell>Bill day</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drafts.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={{ minWidth: 140 }}>
                        <FormControl size="small" fullWidth>
                          <Select
                            value={row.provider}
                            onChange={(e) =>
                              updateDraft(row.id, { provider: e.target.value })
                            }
                          >
                            {AI_SUBSCRIPTION_PROVIDERS.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {p.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.planName}
                          onChange={(e) =>
                            updateDraft(row.id, { planName: e.target.value })
                          }
                          placeholder="Gemini Pro"
                        />
                      </TableCell>
                      <TableCell sx={{ width: 110 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.monthlyCostUsd}
                          onChange={(e) =>
                            updateDraft(row.id, {
                              monthlyCostUsd: e.target.value,
                            })
                          }
                          placeholder="20"
                        />
                      </TableCell>
                      <TableCell sx={{ width: 90 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={row.billingDayOfMonth}
                          onChange={(e) =>
                            updateDraft(row.id, {
                              billingDayOfMonth: e.target.value,
                            })
                          }
                          placeholder="—"
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={row.active !== false}
                              onChange={(e) =>
                                updateDraft(row.id, {
                                  active: e.target.checked,
                                })
                              }
                              size="small"
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          aria-label="Remove subscription"
                          onClick={() =>
                            setDrafts((prev) =>
                              prev.filter((r) => r.id !== row.id),
                            )
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {drafts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No subscriptions yet. Add Gemini / Claude plan fees
                          here.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                sx={{ mt: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Active subscriptions this month:{' '}
                  <strong>{formatUsd(activeTotal)}</strong>
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setDrafts((prev) => [...prev, emptyDraft()])
                    }
                  >
                    Add plan
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                    onClick={handleSave}
                  >
                    {saving ? 'Saving…' : 'Save subscriptions'}
                  </Button>
                </Stack>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Manual ShiftyPM plan cost
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Stripe does not expose invoice dollar amounts yet. Log a manual
            <code> shiftypm_plan </code>
            ledger row for the current UTC month (Reports → Company costs).
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField
              size="small"
              label="Plan label"
              value={manualPlanName}
              onChange={(e) => setManualPlanName(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Amount USD"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              sx={{ width: 140 }}
            />
            <Button
              variant="outlined"
              disabled={manualSaving}
              onClick={handleManualShiftyPm}
            >
              {manualSaving ? 'Saving…' : `Log for ${utcMonthKey()}`}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}
